/**
 * CoverageInfo
 *
 * Displays ingredient coverage breakdown below a meal card:
 * - "You have X/Y ingredients" summary
 * - "Need to buy" box listing missing items (if any)
 * - "You have everything!" message (if fully covered)
 */

import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Paprika } from '../../styles/theme';
import { COVERAGE_COLORS } from './CoverageCircle';
import type { IngredientCoverage } from '../../services/weekGeneratorService';
import { useTranslation } from 'react-i18next';

interface CoverageInfoProps {
  coverage: IngredientCoverage;
  compact?: boolean;
}

export function CoverageInfo({ coverage, compact = false }: CoverageInfoProps) {
  const { t } = useTranslation();
  const { totalCount, toBuyCount, toBuyItems, lufaCount, pantryCount } = coverage;
  const haveCount = lufaCount + pantryCount;

  if (compact) {
    if (toBuyCount === 0) {
      return (
        <Text style={styles.compactAllGood}>{t('thisWeek.allIngredientsCovered')}</Text>
      );
    }
    return (
      <Text style={styles.compactToBuy}>{t('thisWeek.toBuyCount', { count: toBuyCount })}</Text>
    );
  }

  return (
    <View style={styles.container}>
      <Text style={styles.summaryText}>
        {t('thisWeek.youHaveOf', { have: haveCount, total: totalCount })}
      </Text>

      {toBuyCount > 0 ? (
        <View style={styles.toBuyBox}>
          <Text style={styles.toBuyLabel}>
            {t('thisWeek.needToBuyCount', { count: toBuyCount })}
          </Text>
          <Text style={styles.toBuyItems}>
            {toBuyItems.join(' \u00B7 ')}
          </Text>
        </View>
      ) : (
        <Text style={styles.allGoodText}>{t('thisWeek.youHaveEverything')}</Text>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginTop: 6,
  },
  summaryText: {
    fontSize: 11,
    fontWeight: '600',
    color: COVERAGE_COLORS.green,
  },
  toBuyBox: {
    marginTop: 4,
    paddingVertical: 4,
    paddingHorizontal: 8,
    backgroundColor: COVERAGE_COLORS.amberBg,
    borderRadius: 6,
    borderLeftWidth: 3,
    borderLeftColor: COVERAGE_COLORS.amber,
  },
  toBuyLabel: {
    fontSize: 10,
    fontWeight: '600',
    color: '#E65100',
    marginBottom: 2,
  },
  toBuyItems: {
    fontSize: 10,
    color: '#795548',
    lineHeight: 14,
  },
  allGoodText: {
    marginTop: 4,
    fontSize: 11,
    fontWeight: '600',
    color: COVERAGE_COLORS.green,
  },
  compactAllGood: {
    fontSize: 10,
    fontWeight: '600',
    color: COVERAGE_COLORS.green,
  },
  compactToBuy: {
    fontSize: 10,
    fontWeight: '600',
    color: COVERAGE_COLORS.amber,
  },
});
